/**
 * The transcript: days, runs, and the three kinds of row.
 *
 * ── Older messages arrive by being looked at ────────────────────────────────
 * There was a "Load earlier messages" button above the first row, drawn whether
 * there was anything earlier or not. Reading backwards through a long channel
 * is then a press per sixty-four messages. A sentinel at the top of the
 * scroller and an `IntersectionObserver` on it ask for the next page when the
 * ceiling comes into view, and `hasOlder` is what stops both the asking and the
 * sentinel once the conversation is at its floor.
 *
 * ── A prepend must not scroll to the foot, and looks exactly like an arrival ─
 * The scroll effect below runs on `messages.length`, which a prepend increases
 * just as a new message does — so the naive version answers "here is your
 * history" by throwing the reader back to today's last message. The first
 * message's id is what tells the two apart, and the response to a prepend is to
 * put the scroll position back by however much taller the content got. The
 * measurement has to happen before the browser paints, which is why this is a
 * layout effect.
 *
 * ── A call row breaks the run in both directions ────────────────────────────
 * It draws no avatar and no name — its own words already say who — so a
 * message grouped *under* one would lose its header and sit beneath a row that
 * never named anybody, leaving a body floating with no author above it. That
 * only happens when somebody calls between two messages from the same person,
 * which is exactly the kind of thing nobody reproduces by hand. The rule is the
 * desktop app's `showsOwnHeader`, copied: a new day, either side being an
 * event, another author, or more than ten minutes.
 *
 * ── The thread pane is this list too ────────────────────────────────────────
 * Its root and replies, through the same rows and the same grouping, as the
 * desktop's pane reuses its transcript view: a thread laid out differently from
 * the channel would be a second set of bugs. Without `onThread` it is the pane,
 * which loads no history of its own and starts no thread.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useT } from '../../i18n';
import { formatDay } from '../../i18n/format';
import { store, useStore } from '../../lib/relay/store';
import { CallRow } from './CallRow';
import { MeetingCard } from './MeetingCard';
import { MessageRow } from './MessageRow';
import { isOfficialMessage } from '../../lib/relay/official';
import { Empty } from '../Shell';
import type { Message, Person } from '../../lib/relay/types';
import './chat.css';

const DAY = 86_400_000;
const RUN_MS = 600_000;

const dayNumber = (ms: number) => Math.floor(new Date(ms).setHours(0, 0, 0, 0) / DAY);

function startsRun(prev: Message | undefined, cur: Message, newDay: boolean): boolean {
  if (!prev || newDay) return true;
  if (prev.kind !== 'msg' || cur.kind !== 'msg') return true;
  if (prev.senderId !== cur.senderId) return true;
  return cur.at - prev.at > RUN_MS;
}

function DayDivider({ at }: { at: number }) {
  const t = useT();
  const today = dayNumber(Date.now());
  const day = dayNumber(at);
  const label =
    day === today ? t('conversation.today') : day === today - 1 ? t('conversation.yesterday') : formatDay(at);

  return <div className="rl-day">{label}</div>;
}

export function Transcript({
  conversationId,
  messages,
  people,
  onReply,
  onThread
}: {
  conversationId: string;
  messages: Message[];
  people: Person[];
  onReply: (message: Message) => void;
  onThread?: (message: Message) => void;
}) {
  const t = useT();
  const me = useStore((s) => s.me());
  // A boolean, so `useSyncExternalStore`'s Object.is comparison is stable —
  // selecting the conversation object itself would re-render on every change
  // to any of its fields.
  const direct = useStore(
    (s) => s.conversations().find((c) => c.id === conversationId)?.kind === 'direct'
  );
  const older = useStore((s) => s.hasOlder(conversationId));
  const hasOlder = onThread !== undefined && older;
  const official = useStore(
    (s) => s.conversations().find((c) => c.id === conversationId)?.official === true
  );

  const scroller = useRef<HTMLDivElement>(null);
  const ceiling = useRef<HTMLDivElement>(null);
  const foot = useRef<HTMLDivElement>(null);
  /** A page is in flight. The sentinel stays in view while it is, so without
   *  this every observation is another `pull` for the same range. */
  const loading = useRef(false);
  /** What the top of the list was, and how tall the scroller was under it. */
  const anchor = useRef<{ conversationId: string; firstId?: string; height: number }>({
    conversationId,
    height: 0
  });

  // The newest message is the one being read — unless the update was a prepend,
  // which is the whole of this file's second header note.
  useLayoutEffect(() => {
    const box = scroller.current;
    if (!box) return;
    const was = anchor.current;
    const firstId = messages[0]?.id;
    anchor.current = { conversationId, height: box.scrollHeight, ...(firstId ? { firstId } : {}) };

    const prepended =
      was.conversationId === conversationId && was.firstId !== undefined && was.firstId !== firstId;
    if (prepended) {
      box.scrollTop += box.scrollHeight - was.height;
      return;
    }
    foot.current?.scrollIntoView({ block: 'end' });
    // The length, not the array: `messages` is a fresh array after any change
    // the store notifies at all, and scrolling to the foot when somebody
    // reacted to a message three screens up is its own bug.
  }, [conversationId, messages.length]);

  // A fresh observer per page is deliberate: one that stays put reports only a
  // *change* of intersection, so a page shorter than the viewport would leave
  // the sentinel visible and nothing would ask for the next one.
  useEffect(() => {
    // The guard is cleared here rather than only when the promise settles,
    // because `loadOlder` settles on a `batch` that a dropped socket never
    // delivers — a guard that could only be cleared by the answer would wedge
    // the transcript for the rest of the session. The cost is a second `pull`
    // for a range already in flight when a message happens to arrive while it
    // is, which the store dedups by seq.
    loading.current = false;

    const box = scroller.current;
    const target = ceiling.current;
    if (!box || !target || !hasOlder) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (loading.current || !entries.some((e) => e.isIntersecting)) return;
        loading.current = true;
        void store.loadOlder(conversationId).finally(() => {
          loading.current = false;
        });
      },
      // Ask while the ceiling is still below the reader, so the page is there
      // by the time they reach it.
      { root: box, rootMargin: '300px 0px 0px 0px' }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [conversationId, hasOlder, messages.length]);

  if (messages.length === 0) {
    return <Empty title={t('conversation.empty')} />;
  }

  const byId = new Map(people.map((p) => [p.id, p]));

  return (
    <div
      ref={scroller}
      className="mv-scroll mv-page"
      style={{ flex: 1, padding: '0 0 var(--space-12)' }}
    >
      {hasOlder && <div ref={ceiling} style={{ height: 1 }} />}

      {messages.map((m, i) => {
        const prev = messages[i - 1];
        const newDay = !prev || dayNumber(prev.at) !== dayNumber(m.at);

        return (
          <div key={m.id}>
            {newDay && <DayDivider at={m.at} />}
            {m.kind === 'callend' && m.call ? (
              <CallRow call={m.call} at={m.at} own={m.senderId === me?.id} />
            ) : m.kind === 'meeting' && m.meeting ? (
              <MeetingCard meeting={m.meeting} />
            ) : (
              <MessageRow
                message={m}
                {...(byId.get(m.senderId) ? { author: byId.get(m.senderId)! } : {})}
                lead={startsRun(prev, m, newDay)}
                {...(me ? { meId: me.id } : {})}
                direct={direct}
                verified={isOfficialMessage(official, m.senderId)}
                readOnly={official}
                faces={people}
                onReply={onReply}
                {...(onThread ? { onThread } : {})}
              />
            )}
          </div>
        );
      })}

      <div ref={foot} />
    </div>
  );
}
